import { transformToSuggestionTransaction } from "@handlewithcare/prosemirror-suggest-changes";
import { atom } from "jotai";
import { Node } from "prosemirror-model";
import { TextSelection } from "prosemirror-state";
import { type Step } from "prosemirror-transform";

import { parsePage, serializePage } from "@moment/api-collab";
import { Logger } from "@moment/logging";
import {
	buildAcceptedProjectionWithMapping,
	inefficientHasSuggestionMarks,
	schema,
} from "@moment/prosemirror-schema";

import {
	$editorState,
	$editorStateMergeInfo,
	$updateMergeInfo,
} from "../prosemirror/editor-state.atom";
import { editorStateKeyForDocsRoute } from "../prosemirror/editorStateKey";
import { createDiffSteps } from "./create-diff-steps";
import { coalesceRedundantSuggestions } from "./normalize-suggestion-ranges";

const logger = new Logger("apply-external-change");

export interface ApplyExternalChangeInput {
	documentId: string;
	pageId: string;
	content: string;
	/** When true, apply changes directly without wrapping in suggestion marks. */
	skipSuggestions?: boolean;
}

export const $applyExternalChange = atom(null, (get, set, input: ApplyExternalChangeInput) => {
	const { documentId, pageId, content, skipSuggestions } = input;
	const editorStateKey = editorStateKeyForDocsRoute({ documentId, pageId });

	try {
		const currentState = get($editorState(editorStateKey));
		if (!currentState) {
			logger.debug("Editor not loaded, skipping external change", { documentId, pageId });
			return;
		}

		const currentDoc = currentState.doc;
		const currentMarkdown = serializePage(currentDoc);
		if (currentMarkdown === content) {
			logger.debug("Content unchanged, skipping", { documentId, pageId });
			return;
		}

		const { doc: newDocJson } = parsePage(content);
		const newDoc = Node.fromJSON(schema, newDocJson);

		// Validate both docs are proper ProseMirror nodes
		if (!currentDoc || !currentDoc.type || !newDoc || !newDoc.type) {
			logger.error("Invalid document nodes", {
				documentId,
				pageId,
				hasCurrentDoc: !!currentDoc,
				hasNewDoc: !!newDoc,
			});
			return;
		}

		if (currentDoc.eq(newDoc)) {
			logger.debug("PM docs are equal despite markdown diff, skipping", {
				documentId,
				pageId,
			});
			return;
		}

		// Get 3-way merge info for rebasing external changes onto local edits.
		const mergeInfo = get($editorStateMergeInfo(editorStateKey));
		const { lastPersistedDoc, foreignSteps } = mergeInfo ?? {};

		let tr = currentState.tr;

		// Check if the current doc has existing suggestion marks.
		// If so, we need to use projection-based diffing to get correct positions.
		const hasExistingSuggestions = inefficientHasSuggestionMarks(currentDoc);
		let steps: Step[];

		if (hasExistingSuggestions) {
			// Projection-based diffing for documents with existing suggestions:
			//
			//   1. Build "accepted projection" - what the doc would look like if all
			//      suggestions were accepted (deletions removed, insertion marks stripped).
			//   2. Diff the projection against newDoc to get steps in clean positions.
			//   3. Remap those steps back into the marked-up doc's coordinate space.
			const { projection, projectionToMarked } =
				buildAcceptedProjectionWithMapping(currentDoc);

			const projectionSteps = createDiffSteps(projection, newDoc);

			// Remap each step from projection space to marked-up doc space
			steps = [];
			for (const step of projectionSteps) {
				const mappedStep = step.map(projectionToMarked);
				if (mappedStep) {
					steps.push(mappedStep);
				} else {
					logger.debug("Step was deleted by projection mapping", {
						documentId,
						pageId,
					});
				}
			}

			logger.debug("Using projection-based diffing", {
				documentId,
				pageId,
				projectionStepCount: projectionSteps.length,
				remappedStepCount: steps.length,
			});
		} else {
			// Direct diff from currentDoc to newDoc using minimal atomic steps.
			// This approach generates independent steps that work correctly with
			// transformToSuggestionTransaction, avoiding the overlapping/sequential
			// steps that recreateTransform produces.
			steps = createDiffSteps(currentDoc, newDoc);
		}

		if (steps.length === 0) {
			logger.debug("No steps generated from diff", { documentId, pageId });
			// Still update merge info
			if (lastPersistedDoc && foreignSteps) {
				set($updateMergeInfo, { key: editorStateKey, lastPersistedDoc: newDoc });
			}
			return;
		}

		for (const step of steps) {
			const result = tr.maybeStep(step);
			if (result.failed) {
				logger.debug("Direct step failed to apply", {
					documentId,
					pageId,
					reason: result.failed,
				});
			}
		}

		logger.info("Applied external file change (direct mode)", {
			documentId,
			pageId,
			stepCount: steps.length,
			usedProjection: hasExistingSuggestions,
		});

		// Only apply if we have changes
		if (!tr.docChanged) {
			logger.debug("No document changes after merge", { documentId, pageId });
			// Still update merge info since the external content represents the new disk state
			if (lastPersistedDoc && foreignSteps) {
				set($updateMergeInfo, { key: editorStateKey, lastPersistedDoc: newDoc });
			}
			return;
		}

		try {
			const mappedSelection = currentState.selection.map(tr.doc, tr.mapping);
			tr = tr.setSelection(mappedSelection);
		} catch {
			const safePos = Math.min(currentState.selection.anchor, tr.doc.content.size);
			tr = tr.setSelection(TextSelection.create(tr.doc, safePos));
		}

		tr.setMeta("externalChange", true);

		let finalTr;
		if (skipSuggestions) {
			// Apply changes directly without suggestion marks (e.g., during collab conflict resolution)
			finalTr = tr;
		} else {
			// Try to transform into a suggestion transaction for tracked changes. If transformation
			// fails (e.g., complex structural changes that the library can't handle), fall back to
			// applying the original transaction.
			finalTr = transformToSuggestionTransaction(tr, currentState);

			// Coalesce redundant del/ins pairs where the same text was deleted and re-added.
			// This prevents confusing UX where users see "<del>foo</del><ins>foo</ins>".
			finalTr = coalesceRedundantSuggestions(finalTr);
		}

		set($editorState(editorStateKey), finalTr);

		// Update merge info: the new lastPersistedDoc is the external content (newDoc), and
		// foreignSteps are cleared because the transaction we just applied will re-accumulate any
		// user changes made during this apply.
		if (lastPersistedDoc && foreignSteps) {
			set($updateMergeInfo, { key: editorStateKey, lastPersistedDoc: newDoc });
		}
	} catch (err) {
		logger.error("Failed to apply external change", { documentId, pageId, err });
	}
});
$applyExternalChange.debugLabel = "applyExternalChange";
